import React, { useEffect } from 'react'
import { FONTS, fontStack } from '../../../../marketing-v2/features/email-composer/email/theme'
import { hapticTick, hapticSuccess } from '../../../../../_utils/haptics'
import { useBrand, useRecall, saveBrand, restoreBrand, saveBrandExtras, restoreBrandExtras } from '../useBeu'
import { Tick, Palette, Undo } from './icons'
import { BRAND_PATH } from './EmailWidget'

// A font Bailey names, as one of the brand kit's fonts ("Playfair" isn't one;
// "DM Serif Display" is)
export const fontKey = (name) => {
  if (!name) return null
  const n = String(name).toLowerCase().replace(/[^a-z]/g, '')
  return Object.keys(FONTS).find(k => k.toLowerCase() === n || FONTS[k].label.toLowerCase().replace(/[^a-z]/g, '') === n) || null
}
// Words on a colour: white on dark, ink on light
export const inkOn = (hex) => {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || '')
  if (!m) return '#FFFFFF'
  const n = parseInt(m[1], 16)
  const lum = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255 // eslint-disable-line no-bitwise
  return lum > 0.6 ? '#1A1A1E' : '#FFFFFF'
}

// The web fonts a sample needs, loaded once
const loadFont = (key) => {
  const f = FONTS[key]
  if (!f || !f.google || typeof document === 'undefined') return
  const id = `bw-font-${key}`
  if (document.getElementById(id)) return
  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = `https://fonts.googleapis.com/css2?family=${f.google}&display=swap`
  document.head.appendChild(link)
}

// Brand colours, fonts and tone Bailey suggests, next to what's saved now.
// Saved as the brand kit (Marketing › Config) only when the salon says so,
// with Undo.
const BrandWidget = ({ block, id, compact, go }) => {
  const brand = useBrand()
  const [memo, setMemo] = useRecall(id)
  const colours = block.colours || []
  const accent = colours[0] || null
  const palette = colours.slice(1)
  const display = block.fonts && fontKey(block.fonts.display)
  const text = block.fonts && fontKey(block.fonts.text)
  const unknownFonts = block.fonts ? [block.fonts.display, block.fonts.text].filter((f, i) => f && ![display, text][i]) : []
  const savedAt = memo && memo.savedAt
  useEffect(() => { [display, text].forEach(loadFont) }, [display, text])

  const save = () => {
    hapticSuccess()
    const before = { kit: brand.saved || {}, extras: brand.extras || {} }
    const next = { ...(brand.saved || {}), ...(accent ? { accent } : {}), ...(display ? { display } : {}), ...(text ? { text } : {}) }
    saveBrand(brand.salonId, next)
    if (block.tone || palette.length) saveBrandExtras(brand.salonId, { ...(brand.extras || {}), ...(block.tone ? { tone: block.tone } : {}), ...(palette.length ? { palette } : {}) })
    setMemo({ savedAt: Date.now(), before })
  }
  const undo = () => {
    hapticTick()
    const before = (memo && memo.before) || { kit: {}, extras: {} }
    restoreBrand(brand.salonId, before.kit || {})
    restoreBrandExtras(brand.salonId, before.extras || {})
    setMemo({ savedAt: null, before: null })
  }

  const nowAccent = brand.spec && brand.spec.accent
  const shown = accent || nowAccent || '#29282D'
  return (
    <div className={`bw-card is-brand${savedAt ? ' is-done' : ''}`} role="group" aria-label={block.title || 'Your brand'}>
      <div className="bw-head">
        <span className="bw-kind"><Palette />{block.title || 'Your brand'}</span>
        {savedAt && <span className="bw-status is-done"><Tick size={12} />Saved as your brand</span>}
      </div>
      <div className="bw-brand">
        {colours.length > 0 && (
          <div className="bw-swatches" aria-label="Colours">
            {nowAccent && accent && nowAccent.toUpperCase() !== accent && !savedAt && (
              <span className="bw-swatch is-was" title={`Now ${nowAccent}`}><span style={{ background: nowAccent }} /><em>Now</em></span>
            )}
            {colours.map((c, i) => (
              <span key={c} className={`bw-swatch${i === 0 ? ' is-main' : ''}`} title={c}><span style={{ background: c }} /><em>{i === 0 ? 'Brand colour' : c}</em></span>
            ))}
          </div>
        )}
        <div className="bw-sample" style={{ fontFamily: text ? fontStack(text) : undefined }}>
          <p className="bw-sample-head" style={{ fontFamily: display ? fontStack(display) : undefined }}>{brand.salonName || 'Your salon'}</p>
          {!compact && <p className="bw-sample-body">Your next good hair day starts here.</p>}
          <span className="bw-sample-btn" style={{ background: shown, color: inkOn(shown) }}>Book now</span>
        </div>
        {(display || text) && (
          <p className="bw-line">Fonts: {[display && `${FONTS[display].label} for headlines`, text && `${FONTS[text].label} for text`].filter(Boolean).join(', ')}</p>
        )}
        {unknownFonts.length > 0 && <p className="bw-hint">{unknownFonts.join(' and ')} {unknownFonts.length > 1 ? 'aren’t' : 'isn’t'} in the brand kit yet, so the closest saved fonts stay.</p>}
        {block.tone && <blockquote className="bw-tone">{block.tone}</blockquote>}
      </div>
      <div className="bw-actions">
        {savedAt ? (
          <>
            <button type="button" className="bw-btn is-ghost" onClick={undo}><Undo />Undo</button>
            <button type="button" className="bw-btn is-primary" onClick={() => { hapticTick(); go(BRAND_PATH) }}>Open brand kit</button>
          </>
        ) : (
          <>
            <button type="button" className="bw-btn is-secondary" onClick={() => { hapticTick(); go(BRAND_PATH) }}>Open brand kit</button>
            <button type="button" className="bw-btn is-primary" onClick={save}>Save as my brand</button>
          </>
        )}
      </div>
    </div>
  )
}

export default BrandWidget
